import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
// --- Data ---
const NAMES = ['María', 'Carlos', 'Ana', 'José', 'Laura', 'Diego', 'Valentina', 'Andrés', 'Camila', 'Fernando', 'Sofía', 'Miguel', 'Isabella', 'Roberto', 'Daniela', 'Alejandro', 'Gabriela', 'Luis', 'Natalia', 'Ricardo', 'Paola', 'Eduardo', 'Mariana', 'Sebastián', 'Carolina', 'Javier', 'Lucía', 'Tomás', 'Andrea', 'Mateo'];
const CITIES = ['Miami', 'Houston', 'Los Angeles', 'Ciudad de México', 'Bogotá', 'Lima', 'Buenos Aires', 'Santiago', 'Medellín', 'Guadalajara', 'Quito', 'San José', 'Panamá', 'Santo Domingo', 'Monterrey'];
const PRODUCTS = [{
  name: 'Guía Maestra (3 Días)',
  price: '$9 USD',
  weight: 60,
  type: 'icon'
}, {
  name: 'Reprograma tu Mente y Activa tu Riqueza',
  price: '$7 USD',
  weight: 25,
  type: 'image',
  image: 'https://cdn.magicpatterns.com/uploads/8pkHRJ2hPsez5MNb1u6y4x/Reprogramacionpng.png'
}, {
  name: 'Deja de Sanar y Comienza a Cerrar',
  price: '$7 USD',
  weight: 15,
  type: 'image',
  image: 'https://cdn.magicpatterns.com/uploads/x9HMusLw9ZiiSawuTgSm1J/Hombre.png'
}];
// --- Helper Functions ---
const getRandomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const getRandomItem = <T,>(array: T[]): T => array[Math.floor(Math.random() * array.length)];
const getWeightedProduct = () => {
  const totalWeight = PRODUCTS.reduce((sum, p) => sum + p.weight, 0);
  let random = Math.random() * totalWeight;
  for (const product of PRODUCTS) {
    if (random < product.weight) return product;
    random -= product.weight;
  }
  return PRODUCTS[0];
};
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: (typeof PRODUCTS)[0];
  timeAgo: number;
}
export function RecentPurchaseToast() {
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null);
  const [usedNames, setUsedNames] = useState<string[]>([]);
  const shouldReduceMotion = useReducedMotion();
  const generateToast = useCallback(() => {
    // Filter out recently used names
    let availableNames = NAMES.filter(n => !usedNames.includes(n));
    if (availableNames.length === 0) {
      // Fallback if all names are used (shouldn't happen with 30 names and history of 10)
      availableNames = NAMES;
    }
    const name = getRandomItem(availableNames);
    const city = getRandomItem(CITIES);
    const product = getWeightedProduct();
    const timeAgo = getRandomInt(1, 15);
    // Update used names history (keep last 10)
    setUsedNames(prev => {
      const newHistory = [...prev, name];
      if (newHistory.length > 10) return newHistory.slice(newHistory.length - 10);
      return newHistory;
    });
    setCurrentToast({
      id: Date.now(),
      name,
      city,
      product,
      timeAgo
    });
  }, [usedNames]);
  useEffect(() => {
    let showTimeout: NodeJS.Timeout;
    let hideTimeout: NodeJS.Timeout;
    const scheduleNextToast = (delay: number) => {
      showTimeout = setTimeout(() => {
        generateToast();
        // Hide after 4 seconds
        hideTimeout = setTimeout(() => {
          setCurrentToast(null);
          // Schedule next toast between 8-15 seconds after hiding
          scheduleNextToast(getRandomInt(8000, 15000));
        }, 4000);
      }, delay);
    };
    // Initial toast after 3 seconds
    scheduleNextToast(3000);
    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [generateToast]);
  const handleClose = () => {
    setCurrentToast(null);
  };
  // Animation variants
  const variants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 50,
      scale: shouldReduceMotion ? 0.95 : 1
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 300
      }
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4 pointer-events-none" data-id="element-337">
      <AnimatePresence mode="wait" data-id="element-338">
        {currentToast && <motion.div key={currentToast.id} variants={variants} initial="hidden" animate="visible" exit="exit" className="bg-[#1a1f3a] border border-white/10 rounded-xl shadow-2xl p-3 flex items-center gap-3 pointer-events-auto relative overflow-hidden" data-id="element-339">
            {/* Subtle glow effect */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400/0 via-yellow-400/50 to-yellow-400/0 opacity-50" data-id="element-340"></div>

            {/* Thumbnail */}
            <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-black/40 border border-white/5 flex items-center justify-center" data-id="element-341">
              {currentToast.product.type === 'image' ? <img src={currentToast.product.image} alt={currentToast.product.name} className="w-full h-full object-cover" data-id="element-342" /> : <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 w-full h-full flex items-center justify-center" data-id="element-343">
                  <Sparkles className="w-6 h-6 text-slate-900" data-id="element-344" />
                </div>}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-6" data-id="element-345">
              <p className="text-sm text-gray-300 leading-tight" data-id="element-346">
                <span className="font-bold text-white" data-id="element-347">
                  {currentToast.name}
                </span>{' '}
                en {currentToast.city} acaba de comprar
              </p>
              <p className="text-sm font-semibold text-yellow-400 truncate mt-0.5" data-id="element-348">
                {currentToast.product.name}
              </p>
              <div className="flex items-center justify-between mt-1" data-id="element-349">
                <span className="text-xs text-gray-500" data-id="element-350">
                  hace {currentToast.timeAgo} min
                </span>
                <span className="text-xs font-bold text-white/80 bg-white/10 px-1.5 py-0.5 rounded" data-id="element-351">
                  {currentToast.product.price}
                </span>
              </div>
            </div>

            {/* Close button */}
            <button onClick={handleClose} className="absolute top-2 right-2 text-gray-500 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10" aria-label="Cerrar notificación" data-id="element-352">
              <X className="w-3.5 h-3.5" data-id="element-353" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}